import { useRequiredContext } from "@redotech/react-util/context";
import { PaymentProviderType } from "@redotech/redo-billing-common/billing-engine-types";
import {
  BillingPeriod,
  billingPeriodToFriendlyUpper,
} from "@redotech/redo-billing-common/service-invoice";
import { teamGetPaymentMethod } from "@redotech/redo-team-common/team";
import { RedoIncrementDecrement } from "@redotech/redo-web/arbiter-components/increment-decrement/redo-increment-decrement";
import { RedoRadioGroup } from "@redotech/redo-web/arbiter-components/radio/redo-radio-group";
import { RedoToggle } from "@redotech/redo-web/arbiter-components/toggle/redo-toggle";
import { DateInput } from "@redotech/redo-web/date-picker";
import { LabeledInput } from "@redotech/redo-web/labeled-input";
import { Text } from "@redotech/redo-web/text";
import { getDateStartOfDayInTimezone } from "@redotech/util/date";
import { memo, useEffect, useMemo } from "react";
import { TeamContext } from "../../../../team";
import { SubscriptionBillingForm } from "./subscription-billing-form";

export const SubscriptionBillingCardV2 = memo(
  function SubscriptionBillingCardV2({
    input,
  }: {
    input: SubscriptionBillingForm;
  }) {
    const team = useRequiredContext(TeamContext);
    const teamTimezone = team._shopify?.iana_timezone || "America/Los_Angeles";
    const now = getDateStartOfDayInTimezone(new Date(), teamTimezone);
    const {
      enabled,
      startDate,
      isStartDateInFuture,
      renewOnPeriodEnd,
      periodFeeInDollars,
      period,
      paymentProvider,
    } = input.inputs;

    const subscriptionAlreadyStarted = useMemo(() => {
      const alreadyEnabled = !enabled.changed && enabled.value;

      if (!alreadyEnabled) {
        return false;
      }

      const startDateIsTodayOrBefore =
        !!startDate.value &&
        getDateStartOfDayInTimezone(startDate.value, teamTimezone).getTime() <=
          now.getTime();

      if (startDateIsTodayOrBefore) {
        return true;
      }

      return false;
    }, [teamTimezone, startDate, enabled, now]);

    const shouldDisableToggle = useMemo(() => {
      const startDateIsInPast =
        !!startDate.value &&
        getDateStartOfDayInTimezone(startDate.value, teamTimezone).getTime() <
          now.getTime();

      return enabled.value && startDateIsInPast;
    }, [enabled, startDate, now, teamTimezone]);

    useEffect(() => {
      const startDateIsNew = startDate.changed;
      if (!startDateIsNew || !startDate.value) {
        if (!isStartDateInFuture.value) {
          isStartDateInFuture.setValue(true);
        }
        return;
      }

      const startDateIsInThePast =
        getDateStartOfDayInTimezone(startDate.value, teamTimezone).getTime() <
        getDateStartOfDayInTimezone(now, teamTimezone).getTime();

      if (isStartDateInFuture.value !== !startDateIsInThePast) {
        isStartDateInFuture.setValue(!startDateIsInThePast);
      }
    }, [startDate, isStartDateInFuture, now, teamTimezone]);

    return (
      <>
        <RedoToggle
          description="Enable subscription billing. Once subscription has started, disable by turning off auto renewal."
          disabled={shouldDisableToggle}
          label="Enabled"
          setValue={(val) => {
            enabled.setValue(val);
            renewOnPeriodEnd.setValue(val);
            if (!val) {
              startDate.setValue(null);
            }
          }}
          size="sm"
          value={enabled.value}
        />
        <RedoToggle
          description="Automatically renew subscription at the end of each billing period"
          disabled={!enabled.value}
          label="Auto renewal"
          setValue={renewOnPeriodEnd.setValue}
          size="sm"
          value={renewOnPeriodEnd.value}
        />
        <RedoIncrementDecrement
          description="How much to charge each period, in dollars"
          disabled={!enabled.value}
          fullWidth
          label="Billing period fee"
          min={0}
          prefix="$"
          setValue={periodFeeInDollars.setValue}
          step={10}
          value={periodFeeInDollars.value}
        />
        <LabeledInput
          description="The date when subscription billing will begin"
          label="Start date"
        >
          <DateInput
            disabled={!enabled.value || subscriptionAlreadyStarted}
            value={
              startDate.value
                ? (() => {
                    const storedDate = startDate.value;
                    const zonedDateTime =
                      Temporal.Instant.fromEpochMilliseconds(
                        storedDate.getTime(),
                      ).toZonedDateTimeISO(teamTimezone);
                    return new Date(
                      zonedDateTime.year,
                      zonedDateTime.month - 1,
                      zonedDateTime.day,
                    );
                  })()
                : null
            }
            valueChange={(date) => {
              if (!date) {
                startDate.setValue(date);
              } else {
                startDate.setValue(
                  getDateStartOfDayInTimezone(date, teamTimezone),
                );
              }
            }}
          />
        </LabeledInput>
        <RedoRadioGroup
          description="How often to charge for the subscription"
          disabled={!enabled.value}
          label="Billing period"
          options={Object.values(BillingPeriod).map((periodValue) => ({
            id: periodValue,
            label: billingPeriodToFriendlyUpper[periodValue],
          }))}
          setValue={period.setValue}
          value={period.value}
        />
        <RedoRadioGroup
          description="Select which payment provider to use for billing"
          disabled={!enabled.value}
          label="Payment provider"
          options={Object.values(PaymentProviderType).map((provider) => ({
            id: provider,
            label:
              provider === PaymentProviderType.SHOPIFY
                ? "Shopify"
                : provider === PaymentProviderType.STRIPE
                  ? "Stripe"
                  : provider,
          }))}
          setValue={paymentProvider.setValue}
          value={paymentProvider.value}
        />
        {paymentProvider.value === PaymentProviderType.STRIPE &&
          !teamGetPaymentMethod(team.stripe ?? undefined) && (
            <Text fontSize="xs" textColor="warning">
              Team has no stripe payment method configured. Billing will fail
              until the team adds a payment method.
            </Text>
          )}
      </>
    );
  },
);
